import { useInView } from './hooks/useInView';

const SDKS = [
  { name: 'Nomo-WebOn-Kit', desc: 'Build integrated dApps for the NOMO ecosystem' },
  { name: 'Nomo-UI-Kit',    desc: 'Design-system components for WebOn development' },
  { name: 'WalletKit-Dart', desc: 'Cross-platform wallet integration in Dart/Flutter' },
  { name: 'Nomo-ID',        desc: 'Decentralized authentication & identity layer' },
  { name: 'WebOn-SDK',      desc: 'Full SDK for advanced WebOn functionality' },
];

export default function Build() {
  const [ref, inView] = useInView();

  return (
    <section className="build section--dark" id="build" ref={ref}>
      <div className="section-blobs" aria-hidden="true">
        <div className="blob blob--violet" />
        <div className="blob blob--gold" />
      </div>

      <div className="container">
        <div className={`reveal ${inView ? 'in-view' : ''}`}>
          <div className="section-label">Build With Us</div>
          <h2 className="section-title section-title--lg" style={{ marginBottom: '1rem', maxWidth: '600px' }}>
            Build once.{' '}
            <span className="gradient-text">Deploy instantly</span>{' '}
            inside the ecosystem.
          </h2>
          <p className="section-body" style={{ marginBottom: '3rem' }}>
            NOMO is API-first and modular by design. Developers gain immediate access to
            100K+ users, native wallet infrastructure, and a decentralized authentication layer.
          </p>
        </div>

        <div className="glass-wrap">
          <div className={`build__inner stagger ${inView ? 'in-view' : ''}`}>
            <div>
              <div
                style={{
                  fontFamily: 'var(--font-secondary)',
                  fontSize: '0.65rem',
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: 'var(--white-dim)',
                  marginBottom: '1rem',
                }}
              >
                Developer Toolkit
              </div>
              <div className="build__sdk-list">
                {SDKS.map((sdk) => (
                  <div className="sdk-item" key={sdk.name}>
                    <span className="sdk-item__name">{sdk.name}</span>
                    <span className="sdk-item__desc">{sdk.desc}</span>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <a
                  href="https://github.com"
                  className="btn btn-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>View on GitHub →</span>
                </a>
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <span
                    style={{
                      fontFamily: 'var(--font-secondary)',
                      fontSize: '0.62rem',
                      color: 'var(--white-dim)',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Open Source · MIT Licensed
                  </span>
                </div>
              </div>

              <div style={{ marginTop: '2.5rem', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  'Integrated SDK — no external dependencies',
                  'API-first architecture — build your way',
                  'Immediate access to 100K+ active users',
                  'Native wallet infrastructure included',
                  'Decentralized authentication layer built-in',
                ].map((item) => (
                  <div
                    key={item}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontFamily: 'var(--font-secondary)',
                      fontSize: '0.78rem',
                      color: 'var(--white-muted)',
                    }}
                  >
                    <span style={{ color: 'var(--gold)', fontSize: '0.6rem', flexShrink: 0 }}>◆</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="build__terminal">
                <div className="build__terminal-bar">
                  <div className="build__terminal-dot" style={{ background: '#FF5F57' }} />
                  <div className="build__terminal-dot" style={{ background: '#FEBC2E' }} />
                  <div className="build__terminal-dot" style={{ background: '#28C840' }} />
                  <div className="build__terminal-title">nomo-webon · main</div>
                </div>
                <div className="build__terminal-body">
                  <span className="build__terminal-line build__terminal-line--comment"># Install the NOMO WebOn SDK</span>
                  <span className="build__terminal-line build__terminal-line--cmd">$ npm install @nomo-app/webon-kit</span>
                  <span className="build__terminal-line build__terminal-line--success">✓ Installed nomo-webon-kit@2.1.4</span>
                  <span className="build__terminal-line">&nbsp;</span>
                  <span className="build__terminal-line build__terminal-line--import">import {'{'} NomoWebOn, NomoID {'}'}</span>
                  <span className="build__terminal-line build__terminal-line--import">&nbsp; from '@nomo-app/webon-kit';</span>
                  <span className="build__terminal-line">&nbsp;</span>
                  <span className="build__terminal-line build__terminal-line--comment">// Initialize your WebOn</span>
                  <span className="build__terminal-line">const webon = <span className="build__terminal-line--cmd">new</span> NomoWebOn{'({'}</span>
                  <span className="build__terminal-line">&nbsp; name: <span className="build__terminal-line--string">'my-webon'</span>,</span>
                  <span className="build__terminal-line">&nbsp; chains: <span className="build__terminal-line--string">['eth', 'bnb', 'sol']</span>,</span>
                  <span className="build__terminal-line">&nbsp; auth: <span className="build__terminal-line--cmd">NomoID</span>.zenpass,</span>
                  <span className="build__terminal-line">{'}'});</span>
                  <span className="build__terminal-line">&nbsp;</span>
                  <span className="build__terminal-line build__terminal-line--success">✓ WebOn registered · Deploy ready</span>
                  <span className="build__terminal-line build__terminal-line--cmd">$ nomo deploy --ecosystem production</span>
                  <span className="build__terminal-line build__terminal-line--success">✓ Live for 100K+ NOMO users</span>
                  <span className="build__terminal-line">&nbsp;<span className="build__terminal-cursor" /></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
